// useLeagueContext.js
// Custom hook to manage league context (settings, status, pricing)
// Consolidates: leagueSettings, leagueStatus, savedPrices, transactionsEnabled, etc.

import { useState, useEffect, useCallback } from 'react';
import { leaguesAPI } from '../../../../services/apiService';

/**
 * Custom hook for managing league context and initialization
 * Consolidates 7+ state variables into one organized hook
 * 
 * @param {string} leagueId - League UUID
 * @param {boolean} isCommissionerMode - Whether commissioner mode is active
 * @returns {Object} League context state and helpers
 */
export const useLeagueContext = (leagueId, isCommissionerMode = false) => {
  // League Configuration State
  const [leagueSettings, setLeagueSettings] = useState({});
  const [leagueStatus, setLeagueStatus] = useState(null); // null = not loaded yet
  const [savedPrices, setSavedPrices] = useState({});
  
  // Transaction State - Initialize based on league prop if available
  // If league status is already known (from parent), use it to set initial state
  const [transactionsEnabled, setTransactionsEnabled] = useState(false);
  const [pricesExist, setPricesExist] = useState(false);
  const [noPricesWarning, setNoPricesWarning] = useState(false);
  const [browseMode, setBrowseMode] = useState(false);
  
  // Initialization State
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);

  /**
   * Load league settings
   */
  const loadLeagueSettings = useCallback(async () => {
    try {
      const response = await leaguesAPI.getLeagueSettings(leagueId);
      if (response.success) {
        setLeagueSettings(response.settings);
      }
    } catch (err) {
      console.error('Error loading league settings:', err);
    } finally {
      setSettingsLoaded(true);
    }
  }, [leagueId]);

  /**
   * Load saved player prices
   */
  const loadSavedPrices = useCallback(async () => {
    try {
      let response;
      
      // Try price status endpoint first
      if (typeof leaguesAPI.checkPriceStatus === 'function') {
        const priceStatus = await leaguesAPI.checkPriceStatus(leagueId);
        if (priceStatus.success) {
          if (priceStatus.has_prices) {
            // Don't set transactionsEnabled here - that's controlled by league status
            setNoPricesWarning(false);
            setPricesExist(true);
          } else {
            // Don't set transactionsEnabled here - that's controlled by league status
            // Only show warning if in setup mode (handled by checkLeagueStatus)
            setPricesExist(false);
          }
          
          if (priceStatus.prices) {
            response = { success: true, prices: priceStatus.prices };
          }
        }
      }
      
      // Try salary settings endpoint
      if (!response && typeof leaguesAPI.getSalarySettings === 'function') {
        const salaryResponse = await leaguesAPI.getSalarySettings(leagueId);
        if (salaryResponse.success && salaryResponse.existing_prices) {
          response = { success: true, prices: salaryResponse.existing_prices };
          setPricesExist(true);
        }
      }
      
      // Try player prices endpoint
      if (!response && typeof leaguesAPI.getPlayerPrices === 'function') {
        response = await leaguesAPI.getPlayerPrices(leagueId);
        if (response && response.success && response.prices) {
          setPricesExist(true);
        }
      }
      
      // Convert prices array to map
      if (response && response.success && response.prices) {
        const priceMap = {};
        response.prices.forEach(p => {
          const playerId = p.ids?.mlb || p.player_id;
          priceMap[playerId] = p.financial?.baseline_price || 1.0;
        });
        setSavedPrices(priceMap);
      } else {
        setSavedPrices({});
        setPricesExist(false);
      }
    } catch (err) {
      console.error('Error loading saved prices:', err);
      setSavedPrices({});
      setPricesExist(false);
    }
  }, [leagueId]);

  /**
   * Check league status and draft type
   * PRIORITY: Check status FIRST to prevent flash of "Transactions Not Available" screen
   */
  const checkLeagueStatus = useCallback(async () => {
    try {
      const response = await leaguesAPI.getLeagueDetails(leagueId);
      if (response.success) {
        const status = response.league.league_status || response.league.status || 'setup';
        
        console.log('🔍 League status check:', { 
          leagueId, 
          status, 
          raw_league_status: response.league.league_status,
          raw_status: response.league.status,
          transactionsEnabled_will_be: status !== 'setup',
          league: response.league 
        });
        
        // Check for draft type
        const draftType = response.league.draft_type || 
                         response.league.draft_settings?.type ||
                         response.league.season_setup?.draft_type ||
                         null;
        
        const isOfflineDraft = draftType === 'offline';
        
        // Set league status FIRST
        if (isOfflineDraft) {
          console.log('✅ Offline draft mode - enabling transactions');
          setLeagueStatus('offline_draft');
          setTransactionsEnabled(true);
          setNoPricesWarning(false);
          setBrowseMode(false);
        } else {
          setLeagueStatus(status);
          // CRITICAL: Enable transactions IMMEDIATELY for active leagues
          // This prevents the flash of "Transactions Not Available" screen
          // Valid statuses: 'setup', 'draft', 'finalizing', 'in_season'
          if (status === 'setup') {
            console.log('⚠️ League status is "setup" - transactions disabled, browse only');
            // Setup phase: transactions disabled, but users can browse
            setTransactionsEnabled(false);
            setNoPricesWarning(true);
            setBrowseMode(false);
          } else {
            // For all other statuses (draft, finalizing, in_season, or any unknown), enable transactions
            // This handles variations like 'in-season', 'inSeason', etc.
            console.log(`✅ League status is '${status}' - enabling transactions`);
            setTransactionsEnabled(true);
            setNoPricesWarning(false);
            setBrowseMode(false);
          }
        }
      } else {
        console.error('❌ getLeagueDetails returned success=false');
      }
    } catch (err) {
      console.error('❌ Error checking league status:', err);
      // On error, don't show warning (assume league is active)
      setTransactionsEnabled(true);
      setNoPricesWarning(false);
    }
  }, [leagueId]);

  /**
   * Allow user to browse even without prices set
   */
  const handleBrowseAnyway = useCallback(() => {
    setBrowseMode(true);
    setNoPricesWarning(false);
  }, []);

  /**
   * Initialize all league context on mount
   * OPTIMIZED: Check league status FIRST to prevent flash
   */
  useEffect(() => {
    if (leagueId && !initialized) {
      setInitialized(true);
      
      const initializeContext = async () => {
        // Force enable transactions if in commissioner mode
        if (isCommissionerMode) {
          setTransactionsEnabled(true);
          setNoPricesWarning(false);
          setBrowseMode(false);
        }
        
        // CRITICAL: Check league status FIRST (fastest check)
        // This prevents the flash of "Transactions Not Available" screen
        await checkLeagueStatus();
        
        // Then load other data in parallel
        await Promise.all([
          loadLeagueSettings(),
          loadSavedPrices()
        ]);
      };
      
      initializeContext();
    }
  }, [leagueId, initialized, isCommissionerMode, loadLeagueSettings, loadSavedPrices, checkLeagueStatus]);

  /**
   * Poll for status updates during setup/pricing
   */
  useEffect(() => {
    if ((leagueStatus === 'setup' || leagueStatus === 'pricing') && !browseMode) {
      const interval = setInterval(() => {
        checkLeagueStatus();
        loadSavedPrices();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [leagueStatus, browseMode, checkLeagueStatus, loadSavedPrices]);

  return {
    // State values
    leagueSettings,
    leagueStatus,
    savedPrices,
    transactionsEnabled,
    pricesExist,
    noPricesWarning,
    browseMode,
    settingsLoaded,
    
    // Functions
    loadLeagueSettings,
    loadSavedPrices,
    checkLeagueStatus,
    handleBrowseAnyway,
    
    // Computed values
    isReady: settingsLoaded && initialized,
    canTransact: transactionsEnabled || browseMode
  };
};
